import { useEffect } from 'react';
import { useRouter } from 'next/router';
import GA4 from './ga4DataLayer';

/**
 * Hook to track page views in Next.js applications
 * Fires on initial load and route changes
 */
export const usePageViewTracking = () => {
  const router = useRouter();

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    // Track initial page view
    if (router?.asPath) {
      GA4.pageView({
        page_title: document.title,
        page_location: window.location.href,
        page_referrer: document.referrer
      });
    }
  }, []); // Only run once on mount

  useEffect(() => {
    // Only run on client side and if router is available
    if (typeof window === 'undefined' || !router?.events) return;
    
    // Track route changes
    const handleRouteChange = (url) => {
      GA4.pageView({
        page_title: document.title,
        page_location: window.location.href,
        page_referrer: document.referrer
      });
    };

    // Listen for route changes
    router.events.on('routeChangeComplete', handleRouteChange);

    // Cleanup
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router?.events]);
};

/**
 * Component wrapper for automatic page view tracking
 */
export const PageViewTracker = () => {
  // Only render on client side
  if (typeof window === 'undefined') {
    return null;
  }
  
  usePageViewTracking();
  return null;
};

/**
 * Manual page view tracking function
 */
export const trackPageView = (customData = {}) => {
  // Only run on client side
  if (typeof window === 'undefined') return;
  
  GA4.pageView({
    page_title: document.title,
    page_location: window.location.href,
    page_referrer: document.referrer,
    ...customData
  });
};